import {
  NATIONAL_ID_CARD_CONFIG,
  type NationalIdCardContext,
  type NationalIdCardData,
  type NationalIdCardExtraction,
} from "@asap/shared/document/document-types/national-id-card";
import { NationalIdCardSchemas } from "@asap/shared/schemas/document/document-types/national-id-card";
import { countryOptions, nationalityOptions } from "@asap/shared/utils/countries";
import { formatFirstName, formatLastName } from "@asap/shared/utils/string";
import { DocumentTypeHandler } from "../handler";

/**
 * National ID Card Handler
 */
export const NationalIdCardHandler = new DocumentTypeHandler<
  NationalIdCardExtraction,
  NationalIdCardData,
  NationalIdCardContext
>({
  // Identity
  documentType: NATIONAL_ID_CARD_CONFIG.documentType,
  documentName: NATIONAL_ID_CARD_CONFIG.documentName,

  // Constraints
  maxFiles: NATIONAL_ID_CARD_CONFIG.maxFiles,
  acceptedMimeTypes: NATIONAL_ID_CARD_CONFIG.acceptedMimeTypes,
  requiredFields: NATIONAL_ID_CARD_CONFIG.requiredFields,

  // API endpoints
  endpoints: {
    extract: "documents/national-id-card/extract",
    warn: "documents/national-id-card/warn",
    verify: "documents/national-id-card/verify",
    store: "documents/national-id-card/store",
  },

  // Response schemas for runtime validation
  schemas: {
    extractResponse: NationalIdCardSchemas.extractResponse,
    warnResponse: NationalIdCardSchemas.warnResponse,
    validateResponse: NationalIdCardSchemas.validateResponse,
    storeResponse: NationalIdCardSchemas.storeResponse,
  },

  // Toast messages
  toastMessages: {
    success: "La carte nationale d'identité a bien été validée.",
  },

  // Form fields
  formFields: [
    {
      key: "last_name",
      label: NATIONAL_ID_CARD_CONFIG.fieldLabels.last_name,
      type: "text",
      colSpan: 1,
    },
    {
      key: "first_name",
      label: NATIONAL_ID_CARD_CONFIG.fieldLabels.first_name,
      type: "text",
      colSpan: 1,
    },
    {
      key: "sex",
      label: NATIONAL_ID_CARD_CONFIG.fieldLabels.sex,
      type: "select",
      options: [
        { value: "male", label: "Masculin" },
        { value: "female", label: "Féminin" },
        { value: "neutral", label: "Neutre" },
      ],
      isSearchable: false,
      colSpan: 1,
    },
    {
      key: "document_number",
      label: NATIONAL_ID_CARD_CONFIG.fieldLabels.document_number,
      type: "text",
      placeholder: "Ex : 123456789",
      colSpan: 1,
    },
    {
      key: "expiration_date",
      label: NATIONAL_ID_CARD_CONFIG.fieldLabels.expiration_date,
      type: "date",
      colSpan: 1,
    },
    {
      key: "issue_date",
      label: NATIONAL_ID_CARD_CONFIG.fieldLabels.issue_date,
      type: "date",
      colSpan: 1,
    },
    {
      key: "birth_date",
      label: NATIONAL_ID_CARD_CONFIG.fieldLabels.birth_date,
      type: "date",
    },
    {
      key: "birth_place_city",
      label: NATIONAL_ID_CARD_CONFIG.fieldLabels.birth_place_city,
      type: "text",
      placeholder: "Ex : Paris",
    },
    {
      key: "birth_place_country_code",
      label: NATIONAL_ID_CARD_CONFIG.fieldLabels.birth_place_country_code,
      type: "select",
      options: countryOptions(),
    },
    {
      key: "nationality_country_code",
      label: NATIONAL_ID_CARD_CONFIG.fieldLabels.nationality_country_code,
      type: "select",
      options: nationalityOptions(),
    },
  ],

  // Transform OCR extraction to form data
  transformExtraction: (extraction: NationalIdCardExtraction): Partial<NationalIdCardData> => {
    // Validation (throws on issues)
    if (!extraction.isValidDocumentType) {
      throw new Error(
        "Les fichiers ajoutés ne semblent pas correspondre à une carte nationale d'identité. Vérifie que ce soit bien le cas."
      );
    }

    if (!extraction.hasRecto) {
      throw new Error("Veuillez ajouter le recto de votre carte d'identité.");
    }

    if (!extraction.hasVerso) {
      throw new Error("Veuillez ajouter le verso de votre carte d'identité.");
    }

    // Transformation (only non-null values)
    const result: Partial<NationalIdCardData> = {};

    if (extraction.first_name) result.first_name = formatFirstName(extraction.first_name);
    if (extraction.last_name) result.last_name = formatLastName(extraction.last_name);
    if (extraction.birth_date) result.birth_date = extraction.birth_date;
    if (extraction.birth_place_city) result.birth_place_city = extraction.birth_place_city;
    if (extraction.birth_place_country_code) result.birth_place_country_code = extraction.birth_place_country_code;
    if (extraction.sex) result.sex = extraction.sex;
    if (extraction.nationality_country_code) result.nationality_country_code = extraction.nationality_country_code;
    if (extraction.issue_date) result.issue_date = extraction.issue_date;
    if (extraction.expiration_date) result.expiration_date = extraction.expiration_date;
    if (extraction.document_number) result.document_number = extraction.document_number;

    return result;
  },
});
